import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Default theme (Meybra Coach Platform)
const defaultTheme = {
  businessName: 'Meybra Coach Platform',
  logoUrl: '',
  coachPhotoUrl: '',
  coachName: '',
  primaryColor: '#D4AF37',
  secondaryColor: '#0F5E3A',
  accentColor: '#FFFFFF'
};

export const ThemeProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(false);
  const [currentAthleteId, setCurrentAthleteId] = useState(null);
  const location = useLocation();

  // Check if we're on a public page (landing, signup, etc.)
  const isPublicPage = ['/', '/coach/signup', '/coach/login', '/login', '/platform-admin/login'].includes(location.pathname) || 
                       location.pathname.startsWith('/invite/');

  const fetchBranding = useCallback(async (athleteId = null) => {
    // On public pages, always use default theme
    if (isPublicPage) {
      setTheme(defaultTheme);
      applyThemeToCSS(defaultTheme);
      return;
    }

    if (!token) {
      setTheme(defaultTheme);
      applyThemeToCSS(defaultTheme);
      return;
    }

    setLoading(true);
    try {
      // Build URL with optional athlete_id parameter
      let url = `${API}/auth/branding`;
      if (athleteId && user?.role === 'parent') {
        url += `?athlete_id=${athleteId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { branding, business_name } = response.data;
      
      const newTheme = {
        businessName: business_name || defaultTheme.businessName,
        logoUrl: branding?.logo_url || '',
        coachPhotoUrl: branding?.coach_photo_url || '',
        coachName: branding?.coach_name || '',
        primaryColor: branding?.primary_color || defaultTheme.primaryColor,
        secondaryColor: branding?.secondary_color || defaultTheme.secondaryColor,
        accentColor: branding?.accent_color || defaultTheme.accentColor
      };
      
      setTheme(newTheme);
      applyThemeToCSS(newTheme);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      setTheme(defaultTheme);
      applyThemeToCSS(defaultTheme);
    }
    setLoading(false);
  }, [token, user, isPublicPage]);

  // Function to refresh branding for a specific athlete (called by AthleteContext)
  const refreshBrandingForAthlete = useCallback((athleteId) => {
    // Always refresh, even if same athlete ID (in case coach changed)
    setCurrentAthleteId(athleteId);
    fetchBranding(athleteId);
  }, [fetchBranding]);

  useEffect(() => {
    fetchBranding(currentAthleteId);
  }, [token, user, isPublicPage, fetchBranding, currentAthleteId]);

  // Apply CSS variables when theme changes
  const applyThemeToCSS = (themeToApply) => {
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHSL = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return null;
      
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    if (themeToApply.primaryColor) {
      const primaryHSL = hexToHSL(themeToApply.primaryColor);
      if (primaryHSL) {
        root.style.setProperty('--primary', primaryHSL);
      }
    }

    if (themeToApply.secondaryColor) {
      const secondaryHSL = hexToHSL(themeToApply.secondaryColor);
      if (secondaryHSL) {
        root.style.setProperty('--secondary', secondaryHSL);
      }
    }
  };

  const updateTheme = (updates) => {
    const newTheme = { ...theme, ...updates };
    setTheme(newTheme);
    if (!isPublicPage) {
      applyThemeToCSS(newTheme);
    }
  };

  const resetToDefault = () => {
    setTheme(defaultTheme);
    applyThemeToCSS(defaultTheme);
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      loading,
      updateTheme,
      resetToDefault,
      defaultTheme,
      isPublicPage,
      refreshBrandingForAthlete
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
